import { createElement } from 'react';
import type { ReactNode } from 'react';
import { Icon } from './ui/Icon.js';
import {
  ToastContext,
  useToastState,
} from '../hooks/useToast.js';
import type { ToastContextValue, ToastVariant } from '../hooks/useToast.js';

export function ToastProvider({ children }: { children: ReactNode }) {
  const state = useToastState();
  return createElement(
    ToastContext.Provider,
    { value: state },
    children,
    createElement(ToastContainer, { state }),
  );
}

/**
 * Spec 230 — Kridt's toast: paper, hairline, 14px, a 3px tone edge on the
 * left. `role="status"` for the outcome that just quietly succeeded,
 * `role="alert"` for the one that needs attention and (per `useToast.ts`)
 * stays on screen until dismissed.
 */
const TONE: Record<ToastVariant, { edge: string; icon: string; role: 'status' | 'alert' }> = {
  success: { edge: 'var(--hub-color-success)', icon: 'bx-check-circle', role: 'status' },
  error: { edge: 'var(--hub-color-danger)', icon: 'bx-error-circle', role: 'alert' },
};

function ToastContainer({ state }: { state: ToastContextValue }) {
  if (state.toasts.length === 0) return null;

  return (
    <div className="fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2">
      {state.toasts.map((toast) => {
        const tone = TONE[toast.variant];
        return (
          <div
            key={toast.id}
            role={tone.role}
            className="hub-plate flex items-center gap-3 border-l-[3px] text-sm text-[var(--hub-color-ink)]"
            style={{ width: 'var(--hub-toast-width)', borderLeftColor: tone.edge }}
          >
            <Icon name={tone.icon} />
            <span className="max-w-xs">{toast.message}</span>
            <button
              type="button"
              onClick={() => state.removeToast(toast.id)}
              aria-label="Dismiss notification"
              className="ml-auto rounded-md p-0.5 hover:bg-[var(--hub-color-surface-sunken)]"
            >
              <Icon name="bx-x" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
